const PLAYER_REGISTERED_TODAY_COUNTER_TYPE = 1;
const PLAYER_ONLINE_COUNTER_TYPE = 2;
const MS_PER_SECOND = 1000;
const SECONDS_PER_DAY = 86400;
const SERVER_UTC_OFFSET_STORAGE_KEY = "player_counter_server_utc_offset_ms";
const DEFAULT_COMPANY_TIMEZONE = 8;

const getStoredServerUtcOffset = () => {
  try {
    const storedOffset = window.sessionStorage.getItem(SERVER_UTC_OFFSET_STORAGE_KEY);
    const offset = Number(storedOffset);
    return Number.isFinite(offset) ? offset : 0;
  } catch (e) {
    return 0;
  }
};

let serverUtcOffsetMs = getStoredServerUtcOffset();

export const isPlayerCounterEnabled = (value) => value === true;

export const setPlayerCounterServerUtc = (serverUtcTimestampMs, clientTimestampMs = Date.now()) => {
  if (Number.isFinite(serverUtcTimestampMs)) {
    serverUtcOffsetMs = serverUtcTimestampMs - clientTimestampMs;

    try {
      window.sessionStorage.setItem(SERVER_UTC_OFFSET_STORAGE_KEY, String(serverUtcOffsetMs));
    } catch (e) {
      // Storage can be unavailable in private modes; the in-memory offset still works.
    }
  }
};

export const getPlayerCounterUtcTimestamp = () => Date.now() + serverUtcOffsetMs;

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const stableRandomRatio = (seed) => {
  let hash = 2166136261;
  const text = String(seed);

  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
};

const getIntervalSeconds = (setting) => {
  const intervalMinutes = toNumber(setting?.generateIntervalMinutes, 1);
  return Math.max(1, Math.round(intervalMinutes * 60));
};

const getCounterRange = (setting) => {
  const minValue = toNumber(setting?.minValue, 0);
  const maxValue = toNumber(setting?.maxValue, minValue);

  return {
    lower: Math.min(minValue, maxValue),
    upper: Math.max(minValue, maxValue)
  };
};

const getCompanyTimezoneOffsetMs = (timeZone) => {
  return toNumber(timeZone, DEFAULT_COMPANY_TIMEZONE) * 60 * 60 * MS_PER_SECOND;
};

const getCompanyDayTime = (utcTimestampMs, timeZone) => {
  const timezoneOffsetMs = getCompanyTimezoneOffsetMs(timeZone);
  const companyDate = new Date(utcTimestampMs + timezoneOffsetMs);
  const secondOfDay = companyDate.getUTCHours() * 3600
    + companyDate.getUTCMinutes() * 60
    + companyDate.getUTCSeconds()
    + (companyDate.getUTCMilliseconds() / 1000);

  return { secondOfDay };
};

const getSmoothOnlineValue = (setting, utcTimestampMs) => {
  const { lower, upper } = getCounterRange(setting);
  const range = upper - lower;

  if (range <= 0) {
    return lower;
  }

  const intervalSeconds = getIntervalSeconds(setting);
  const bucketDurationMs = intervalSeconds * MS_PER_SECOND;
  const absoluteBucket = Math.floor(utcTimestampMs / bucketDurationMs);
  const seed = [
    setting.counterType,
    lower,
    upper,
    intervalSeconds
  ].join(":");

  const phaseA = stableRandomRatio(`${seed}:a`) * Math.PI * 2;
  const phaseB = stableRandomRatio(`${seed}:b`) * Math.PI * 2;
  const phaseC = stableRandomRatio(`${seed}:c`) * Math.PI * 2;

  const wave = (
    Math.sin((absoluteBucket / 17) + phaseA) * 0.55
    + Math.sin((absoluteBucket / 41) + phaseB) * 0.30
    + Math.sin((absoluteBucket / 89) + phaseC) * 0.15
  );
  const centered = lower + (range / 2);
  const value = centered + (wave * range * 0.48);

  return Math.round(clamp(value, lower, upper));
};

const getRegisteredTodayValue = (setting, companyTimeZone, utcTimestampMs) => {
  const { lower, upper } = getCounterRange(setting);
  const range = upper - lower;

  if (range <= 0) {
    return lower;
  }

  const intervalSeconds = getIntervalSeconds(setting);
  const { secondOfDay } = getCompanyDayTime(utcTimestampMs, companyTimeZone);
  const currentBucket = Math.floor(secondOfDay / intervalSeconds);
  const totalBuckets = Math.max(1, Math.ceil(SECONDS_PER_DAY / intervalSeconds));
  const lastBucket = Math.max(1, totalBuckets - 1);
  const progress = clamp(Math.min(currentBucket, lastBucket) / lastBucket, 0, 1);

  return Math.floor(lower + (range * progress));
};

const getNextRefreshMs = (setting, companyTimeZone, utcTimestampMs) => {
  const intervalSeconds = getIntervalSeconds(setting);

  if (setting?.counterType === PLAYER_ONLINE_COUNTER_TYPE) {
    const bucketDurationMs = intervalSeconds * MS_PER_SECOND;
    const nextBucketTimestampMs = (Math.floor(utcTimestampMs / bucketDurationMs) + 1) * bucketDurationMs;

    return Math.max(1000, Math.ceil(nextBucketTimestampMs - utcTimestampMs));
  }

  const { secondOfDay } = getCompanyDayTime(utcTimestampMs, companyTimeZone);
  const nextBucketSecond = Math.min(
    (Math.floor(secondOfDay / intervalSeconds) + 1) * intervalSeconds,
    SECONDS_PER_DAY
  );
  const secondsUntilNextBucket = nextBucketSecond > secondOfDay
    ? nextBucketSecond - secondOfDay
    : intervalSeconds;

  return Math.max(1000, Math.ceil(secondsUntilNextBucket * MS_PER_SECOND));
};

export const normalizePlayerCounterSettings = (settings, companyTimeZone) => {
  if (!Array.isArray(settings)) {
    return [];
  }

  return settings.map((setting) => ({
    ...setting,
    timeZone: setting?.timeZone ?? companyTimeZone
  }));
};

export const getPlayerCounterFromSetting = (setting, companyTimeZone, utcTimestampMs = getPlayerCounterUtcTimestamp()) => {
  if (!setting || !isPlayerCounterEnabled(setting.isEnabled)) {
    return null;
  }

  const timeZone = setting.timeZone ?? companyTimeZone;
  let value = null;

  if (setting.counterType === PLAYER_ONLINE_COUNTER_TYPE) {
    value = getSmoothOnlineValue(setting, utcTimestampMs);
  } else if (setting.counterType === PLAYER_REGISTERED_TODAY_COUNTER_TYPE) {
    value = getRegisteredTodayValue(setting, timeZone, utcTimestampMs);
  }

  if (value === null) {
    return null;
  }

  return {
    counterType: setting.counterType,
    value,
    nextRefreshMs: getNextRefreshMs(setting, timeZone, utcTimestampMs)
  };
};

export const getPlayerCountersFromSettings = (settings, companyTimeZone, utcTimestampMs = getPlayerCounterUtcTimestamp()) => {
  if (!Array.isArray(settings)) {
    return [];
  }

  return settings
    .map((setting) => getPlayerCounterFromSetting(setting, companyTimeZone, utcTimestampMs))
    .filter(Boolean);
};
